import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "mainImage", 
    "counter", 
    "prevBtn", 
    "nextBtn", 
    "modal", 
    "modalImage", 
    "modalCounter", 
    "modalPrevBtn", 
    "modalNextBtn"
  ]
  
  connect() {
    this.currentImageIndex = 0
    this.images = this.extractImageUrls()
    this.modal = null
    
    // Initialize the main image and counter
    this.updateMainImage()
    this.updateCounter()
  }
  
  // Extract image URLs from thumbnails, fallback to main image if no thumbnails
  extractImageUrls() {
    const thumbnails = this.element.querySelectorAll('.property-thumbnail')
    
    if (thumbnails.length > 0) {
      return Array.from(thumbnails).map(thumb => thumb.dataset.imageSrc)
    } else {
      // Fallback: extract from main image if no thumbnails (single image case)
      const mainImage = this.hasMainImageTarget ? this.mainImageTarget.src : null
      return mainImage ? [mainImage] : []
    }
  }
  
  // Handle thumbnail clicks
  selectImage(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.imageIndex)
    this.currentImageIndex = index
    this.updateMainImage()
    this.updateCounter()
    this.updateThumbnailActiveState()
  }
  
  // Navigate to previous image
  previousImage(event) {
    event.preventDefault()
    this.currentImageIndex = this.currentImageIndex > 0 ? 
      this.currentImageIndex - 1 : 
      this.images.length - 1
    this.updateMainImage()
    this.updateCounter()
    this.updateThumbnailActiveState()
  }
  
  // Navigate to next image
  nextImage(event) {
    event.preventDefault()
    this.currentImageIndex = this.currentImageIndex < this.images.length - 1 ? 
      this.currentImageIndex + 1 : 
      0
    this.updateMainImage()
    this.updateCounter()
    this.updateThumbnailActiveState()
  }
  
  // Open lightbox modal
  openLightbox(event) {
    event.preventDefault()
    
    // Clean up any existing modal backdrops
    this.cleanupModalBackdrops()
    
    if (!this.modal) {
      this.modal = new Modal(this.modalTarget, {
        backdrop: false, // Disable Bootstrap's backdrop
        keyboard: true,
        focus: true
      })
    }
    
    // Set the modal image to the current main image
    this.modalImageTarget.src = this.images[this.currentImageIndex]
    this.updateModalCounter()
    
    // Add event listeners to track modal state
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.ensureModalZIndex()
    })
    
    this.modalTarget.addEventListener('hidden.bs.modal', () => {
      this.cleanupModalBackdrops()
    })
    
    this.modal.show()
    
    // Also run z-index fix after a longer delay to ensure modal is fully rendered
    setTimeout(() => {
      this.ensureModalZIndex()
    }, 300)
  }
  
  // Clean up modal backdrops to prevent conflicts
  cleanupModalBackdrops() {
    const backdrops = document.querySelectorAll('.modal-backdrop')
    backdrops.forEach((backdrop) => {
      if (backdrop && !backdrop.classList.contains('show')) {
        backdrop.remove()
      }
    })
  }
  
  // Ensure proper z-index for modal elements
  ensureModalZIndex() {
    const modal = document.getElementById('imageLightbox')
    if (modal) {
      modal.style.zIndex = '9999'
      
      const dialog = modal.querySelector('.modal-dialog')
      if (dialog) {
        dialog.style.zIndex = '10000'
      }
      
      const content = modal.querySelector('.modal-content')
      if (content) {
        content.style.zIndex = '10001'
      }
      
      const buttons = modal.querySelectorAll('.btn')
      buttons.forEach((btn) => {
        // CSS will handle z-index, just ensure pointer events work
        btn.style.pointerEvents = 'auto'
      })
      
      // Check for modal backdrops that might be interfering
      const backdrops = document.querySelectorAll('.modal-backdrop')
      backdrops.forEach((backdrop) => {
        // Ensure backdrop doesn't block clicks
        backdrop.style.pointerEvents = 'none'
      })
    }
  }
  
  // Modal navigation - previous
  modalPreviousImage(event) {
    event.preventDefault()
    this.currentImageIndex = this.currentImageIndex > 0 ? 
      this.currentImageIndex - 1 : 
      this.images.length - 1
    this.modalImageTarget.src = this.images[this.currentImageIndex]
    this.updateModalCounter()
    // Also update main gallery
    this.updateMainImage()
    this.updateCounter()
    this.updateThumbnailActiveState()
  }
  
  // Modal navigation - next
  modalNextImage(event) {
    event.preventDefault()
    this.currentImageIndex = this.currentImageIndex < this.images.length - 1 ? 
      this.currentImageIndex + 1 : 
      0
    this.modalImageTarget.src = this.images[this.currentImageIndex]
    this.updateModalCounter()
    // Also update main gallery
    this.updateMainImage()
    this.updateCounter()
    this.updateThumbnailActiveState()
  }
  
  // Update the main image display
  updateMainImage() {
    if (this.hasMainImageTarget && this.images[this.currentImageIndex]) {
      this.mainImageTarget.src = this.images[this.currentImageIndex]
      this.mainImageTarget.dataset.imageIndex = this.currentImageIndex
    }
  }
  
  // Update the image counter
  updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.currentImageIndex + 1} of ${this.images.length}`
    }
  }
  
  // Update the modal counter
  updateModalCounter() {
    if (this.hasModalCounterTarget) {
      this.modalCounterTarget.textContent = `${this.currentImageIndex + 1} of ${this.images.length}`
    }
  }
  
  // Update thumbnail active state
  updateThumbnailActiveState() {
    const thumbnails = this.element.querySelectorAll('.property-thumbnail')
    thumbnails.forEach((thumb, index) => {
      if (index === this.currentImageIndex) {
        thumb.classList.add('active')
      } else {
        thumb.classList.remove('active')
      }
    })
  }
  
  // Handle keyboard navigation in modal
  modalKeydown(event) {
    if (event.key === 'ArrowLeft') {
      this.modalPreviousImage(event)
    } else if (event.key === 'ArrowRight') {
      this.modalNextImage(event)
    } else if (event.key === 'Escape') {
      this.modal?.hide()
    }
  }
}